import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ExpandIcon } from 'lucide-react';
import { productPages } from '../data/productPages';
import { PageLightbox } from './PageLightbox';
export function PagesCarousel() {
  const reduceMotion = useReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start'
  }, [Autoplay({
    delay: 4000,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
    stopOnFocusIn: true,
    playOnInit: !reduceMotion
  })]);
  const [selected, setSelected] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Pause autoplay while the lightbox is open
  useEffect(() => {
    const autoplay = emblaApi?.plugins().autoplay;
    if (!autoplay) return;
    if (lightboxIndex !== null) autoplay.stop();else if (!reduceMotion) autoplay.play();
  }, [emblaApi, lightboxIndex, reduceMotion]);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollNext();
    }
  };
  const arrowClass = 'grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold text-gold-ink shadow-lift transition-[background-color,transform] duration-150 ease-out hover:bg-gold-hover active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy';
  return <section aria-labelledby="pages-carousel-heading" className="w-full bg-navy" data-id="element-142" data-mp="2hq4t870s7r0n">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-143" data-mp="2co5niqhkki0j">
        <div className="text-center" data-id="element-144" data-mp="2qozwba8j4kzq">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-gold" data-id="element-145" data-mp="2qnynlcezkal6">
            Mirá lo que hay adentro
          </p>
          <h2 id="pages-carousel-heading" className="mt-3 font-display font-bold uppercase text-white text-3xl sm:text-5xl" data-id="element-146" data-mp="30fbea0p1supm">
            Páginas listas para imprimir y correr en la práctica
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/75" data-id="element-147" data-mp="3aek5wmwfxx8t">
            Un vistazo real al interior: el método, los conceptos de defensa y
            las practice cards que vas a tener en la mano toda la temporada.
          </p>
        </div>

        <div className="mt-10 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-navy" role="region" aria-roledescription="carrusel" aria-label="Páginas del producto" tabIndex={0} onKeyDown={onKeyDown} data-id="element-148" data-mp="3oyd0uhkcanhs">
          <div className="overflow-hidden" ref={emblaRef} data-id="element-149" data-mp="35anjz5dxy7nc">
            <div className="-ml-4 flex touch-pan-y" data-id="element-150" data-mp="3a3gobs5hzdyw">
              {productPages.map((page, i) => <div key={page.src} className="min-w-0 shrink-0 grow-0 basis-[80%] pl-4 sm:basis-1/2 lg:basis-1/3" role="group" aria-roledescription="slide" aria-label={`${i + 1} de ${productPages.length}: ${page.label}`} data-id="element-151" data-mp="2wc245hzut6q9" data-mpi={i}>
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Ampliar ${page.label}`} className="group relative block aspect-[9/16] w-full overflow-hidden rounded-2xl border border-navy-line bg-navy-mid shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-152" data-mp="2rb4eylgffcf9">
                    <img src={page.src} alt={page.alt} loading="lazy" decoding="async" draggable={false} className="h-full w-full object-contain" data-id="element-153" data-mp="3fy6wr20er5wv" />
                    <span className="pointer-events-none absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-navy-deep/80 text-white opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-154" data-mp="31yxqau207q63">
                      <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-155" data-mp="v7y7rugaq00p" />
                    </span>
                  </button>
                  <p className="mt-3 text-center text-sm font-semibold text-white/80" data-id="element-156" data-mp="12r7ibr0jg4rc">
                    {page.label}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-5" data-id="element-157" data-mp="1cjwihpqn1w7d">
          <button type="button" onClick={scrollPrev} aria-label="Página anterior" className={arrowClass} data-id="element-158" data-mp="1hg3rjrebzntj">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-159" data-mp="jupl4u2x7cnp" />
          </button>

          <div className="flex items-center gap-2" role="tablist" aria-label="Elegir página" data-id="element-160" data-mp="oshmsftdad53">
            {productPages.map((page, i) => <button key={page.src} type="button" role="tab" aria-selected={i === selected} aria-label={`Ir a ${page.label}`} onClick={() => scrollTo(i)} className="grid h-6 place-items-center px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full" data-id="element-161" data-mp="yq5k9dicxyqy" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === selected ? 'w-6 bg-gold' : 'w-2 bg-white/30'}`} data-id="element-162" data-mp="54hoalpks4vk" />
              </button>)}
          </div>

          <button type="button" onClick={scrollNext} aria-label="Página siguiente" className={arrowClass} data-id="element-163" data-mp="3nhfu3991trxn">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-164" data-mp="3ijnr9k6v8leo" />
          </button>
        </div>
      </div>

      <PageLightbox pages={productPages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-165" data-mp="5wxte04kzkb" />
    </section>;
}